import type { BulkReferralRowResult } from '../../../../types/bulk-referral.type';
import { CareSeekerBadge } from './CareSeekerBadge';
import { findRowErrorValue, formatRole } from './reconciliationHelpers';
import { StatusIndicator } from './StatusIndicator';

type ReconciliationTableRowProps = {
  row: BulkReferralRowResult;
  isSelected: boolean;
  isFlashing: boolean;
  onSelect: () => void;
};

const PersonCell = ({
  person,
  rawEmailColumn,
  row,
}: {
  person: BulkReferralRowResult['referring'];
  rawEmailColumn: 'referring_email' | 'referred_email';
  row: BulkReferralRowResult;
}) => (
  <td className="max-w-[16rem] px-3 py-2">
    {person ? (
      <>
        <p className="truncate text-sm text-gray-900">{person.name}</p>
        <p className="truncate text-xs text-gray-500">
          {formatRole(person.role)}
          {person.company ? ` · ${person.company}` : ''}
        </p>
      </>
    ) : (
      <>
        <p className="truncate text-sm text-red-700">unmatched</p>
        <p className="truncate text-xs text-gray-500">{findRowErrorValue(row, rawEmailColumn) ?? '—'}</p>
      </>
    )}
  </td>
);

/**
 * `referred_on_behalf_of` is optional per row: an empty cell is "no party"
 * (a plain dash), never the red "unmatched" state — that renders only when
 * the column itself carries a row error.
 */
const OnBehalfOfCell = ({ row }: { row: BulkReferralRowResult }) => {
  const person = row.onBehalfOf;
  const erroredValue = findRowErrorValue(row, 'referred_on_behalf_of');
  return (
    <td className="max-w-[14rem] px-3 py-2">
      {person ? (
        <>
          <p className="truncate text-sm text-gray-900">{person.name}</p>
          <p className="truncate text-xs text-gray-500">
            {formatRole(person.role)}
            {person.company ? ` · ${person.company}` : ''}
          </p>
        </>
      ) : erroredValue !== undefined ? (
        <>
          <p className="truncate text-sm text-red-700">unmatched</p>
          <p className="truncate text-xs text-gray-500">{erroredValue || '—'}</p>
        </>
      ) : (
        <p className="truncate text-sm text-gray-400">—</p>
      )}
    </td>
  );
};

const CareSeekerCell = ({ row }: { row: BulkReferralRowResult }) => {
  const careSeeker = row.careSeeker;
  if (!careSeeker || careSeeker.outcome === 'UNRESOLVED') {
    return (
      <td className="max-w-[14rem] px-3 py-2">
        <p className="truncate text-sm text-red-700">unresolved</p>
        <p className="truncate text-xs text-gray-500">
          {findRowErrorValue(row, 'care_seeker_email') ?? findRowErrorValue(row, 'new_care_seeker_first_name') ?? '—'}
        </p>
      </td>
    );
  }
  return (
    <td className="max-w-[14rem] px-3 py-2">
      <p className="truncate text-sm text-gray-900">{careSeeker.outcome === 'NONE' ? '—' : careSeeker.name}</p>
      <CareSeekerBadge outcome={careSeeker.outcome} willSendInvite={careSeeker.willSendInvite} />
    </td>
  );
};

export const ReconciliationTableRow = ({ row, isSelected, isFlashing, onSelect }: ReconciliationTableRowProps) => {
  const hasErrors = row.errors.length > 0;
  return (
    <tr
      id={`reconciliation-row-${row.rowNumber}`}
      onClick={onSelect}
      aria-selected={isSelected}
      className={`cursor-pointer border-b border-gray-100 border-l-2 hover:bg-gray-50 ${
        hasErrors ? 'border-l-red-500' : 'border-l-transparent'
      } ${isFlashing ? 'bg-red-100' : isSelected ? 'bg-[#026857]/5' : ''}`}
    >
      <td className="px-3 py-2 font-mono text-xs text-gray-400">{row.rowNumber}</td>
      <PersonCell person={row.referring} rawEmailColumn="referring_email" row={row} />
      <PersonCell person={row.referred} rawEmailColumn="referred_email" row={row} />
      <OnBehalfOfCell row={row} />
      <CareSeekerCell row={row} />
      <td className="px-3 py-2 text-xs text-gray-600">{row.referralOccurredAt ?? '—'}</td>
      <td className="px-3 py-2">
        <StatusIndicator errorCount={row.errors.length} />
      </td>
      <td className="max-w-[22rem] px-3 py-2 text-xs text-red-700">
        {hasErrors ? <span className="line-clamp-2">{row.errors[0].message}</span> : null}
      </td>
    </tr>
  );
};
